// Claude credential application for child processes the manager spawns.
//
// Resolves the agent's effective Claude auth via /host/exchange and writes it
// into a child process's env before the manager launches `claude -p` (scheduled
// tasks, conversation eval, direct-chat one-shots). Depends only on the API
// client, the CLI config accessor, and the runtime log leaf — no other manager
// module — so it sits in the leaf layer importable by every spawn site.
// See ADR-0011.

import { existsSync, rmSync } from 'node:fs';
import { join } from 'node:path';
import { homedir } from 'node:os';
import { exchangeApiKey } from '../api-client.js';
import { getApiKey } from '../config.js';
import { log } from './runtime.js';

/**
 * Prepare a child-process env for non-tmux one-shot Claude spawns
 * (claude-scheduler + direct-chat). Mirrors what spawnSession() in
 * persistent-session does for the tmux path.
 *
 * - Force-refreshes /host/exchange so mode flips and key rotations are
 *   picked up immediately instead of lagging the ~50m JWT cache.
 * - When mode=api_key, injects the decrypted key AND purges any OAuth
 *   credential files under homedir()/.claude so Claude can't silently
 *   fall back to a stale subscription session.
 * - When mode=subscription, strips ANTHROPIC_API_KEY from the env
 *   (a key inherited from the manager's shell would override OAuth).
 * - Fails closed: throws if mode=api_key but no decrypted key is in
 *   the exchange response, or if the exchange itself errors. Callers
 *   must catch and skip the spawn — never spawn Claude with ambiguous
 *   auth under the wrong mode.
 */
export async function applyClaudeAuthToEnv(
  childEnv: Record<string, string | undefined>,
  label: string,
): Promise<void> {
  const apiKey = getApiKey();
  if (!apiKey) {
    throw new Error('AGT_API_KEY is not set');
  }
  const exchange = await exchangeApiKey(apiKey, false, { forceRefresh: true });

  if (exchange.claudeAuthMode === 'api_key') {
    if (!exchange.anthropicApiKey) {
      throw new Error('claude_auth_mode=api_key but /host/exchange returned no decrypted key');
    }
    childEnv.ANTHROPIC_API_KEY = exchange.anthropicApiKey;
    // Purge OAuth creds for the active user so Claude doesn't silently
    // prefer a stale subscription session. Matches the persistent-session
    // purge (ENG-4417); use homedir() not a hardcoded /root path so this
    // works for non-root and macOS dev setups.
    const claudeDir = join(homedir(), '.claude');
    for (const filename of ['.credentials.json', 'credentials.json']) {
      const p = join(claudeDir, filename);
      if (existsSync(p)) {
        try {
          rmSync(p, { force: true });
          log(`[${label}] Removed ${p} (api_key mode — preventing OAuth fallback)`);
        } catch { /* non-fatal */ }
      }
    }
  } else {
    // subscription mode — strip any inherited key so Claude uses its OAuth login
    delete childEnv.ANTHROPIC_API_KEY;
  }
}
